import React, { useState } from 'react';
import { GiftIcon, HomeIcon, ShieldAlertIcon } from 'lucide-react';
import { useAppState } from '../../contexts/AppStateContext';
import { useUi } from '../../contexts/UiContext';
import { copy } from '../../data/copy';
import { exceptionTypeLabels, excuseLabels } from '../../data/labels';
import { equalShares } from '../../utils/progress';
import { performersOf } from '../../utils/today';
import { formatDate, formatTimeOfDay } from '../../utils/time';
import { Button } from '../ui/Button';
import { EmptyNote } from '../ui/Panel';
import { Avatar } from '../Avatar';
export function ApprovalQueue() {
  const {
    family,
    db,
    reviewQueue,
    memberById,
    getOcc,
    approve,
    approveReward,
    rejectReward,
    redeemReward,
    decideException,
    balanceOf
  } = useAppState();
  const {
    openApproval
  } = useUi();
  const [override, setOverride] = useState<Record<string, boolean>>({});
  const rewards = db.rewardRequests.filter(r => r.familyId === family.id && r.status === 'caka');
  const toRedeem = db.rewardRequests.filter(r => r.familyId === family.id && r.status === 'odobreno');
  const exceptions = db.exceptions.filter(e => e.familyId === family.id && e.status === 'caka');
  const empty = reviewQueue.length + rewards.length + exceptions.length + toRedeem.length === 0;
  const approveAll = () => reviewQueue.forEach(t => {
    approve(t.id, equalShares(t.baseValue ?? 0, performersOf(t, getOcc(t.id))));
  });
  if (empty) return <EmptyNote data-id="element-442" data-mp="zvvp56swlbpa">{copy.emptyQueue}</EmptyNote>;
  return <div className="space-y-5" data-id="element-443" data-mp="vxfvwl68b73f">
      {reviewQueue.length > 0 && <div data-id="element-444" data-mp="qx1nv34mnx5e">
          <div className="mb-2 flex items-center justify-between gap-3" data-id="element-445" data-mp="qwifoe4s4n4k">
            <h3 className="flex items-center gap-2 text-sm font-extrabold" data-id="element-446" data-mp="ly74o02r06kr">
              <HomeIcon className="h-4 w-4 text-review-ink" aria-hidden="true" data-id="element-447" data-mp="m0b8ki0nd5l1" /> Opravila v pregledu ({reviewQueue.length})
            </h3>
            {reviewQueue.length > 1 && <Button size="sm" variant="ghost" onClick={approveAll} data-id="element-448" data-mp="36muxxeot7nhh">
                Potrdi vse (enaki deleži)
              </Button>}
          </div>
          <ul className="divide-y divide-line rounded-2xl border-[1.5px] border-line" data-id="element-449" data-mp="3d4y25q7zreb5">
            {reviewQueue.map(t => {
          const o = getOcc(t.id);
          const by = memberById(o.submittedBy);
          return <li key={t.id} data-id="element-450" data-mp="3426orkvk28gv">
                  <button type="button" onClick={() => openApproval(t.id)} className="flex w-full items-center gap-3 px-3 py-3 text-left transition-colors duration-150 hover:bg-canvas" data-id="element-451" data-mp="2pf3nd3443fo9">
                    {by && <Avatar member={by} size="sm" data-id="element-452" data-mp="2gefcuidxfxg4" />}
                    <span className="min-w-0 flex-1" data-id="element-453" data-mp="2gfqhslj3zb8t">
                      <span className="block font-bold" data-id="element-454" data-mp="2kfr2up0pqkou">{t.title}</span>
                      <span className="block text-xs text-muted" data-id="element-455" data-mp="2kdxxre5r08iy">
                        Oddal(a) {by?.name ?? '–'}
                        {o.submittedAt ? ` ob ${formatTimeOfDay(o.submittedAt)}` : ''} · {t.baseValue ?? 0} točk
                        {performersOf(t, o).length > 1 ? ', deljeno' : ''}
                      </span>
                    </span>
                    <span className="rounded-full bg-review-soft px-2.5 py-1 text-xs font-bold text-review-ink" data-id="element-456" data-mp="1mvirlbsmcasc">Preglej</span>
                  </button>
                </li>;
        })}
          </ul>
        </div>}

      {rewards.length > 0 && <div data-id="element-457" data-mp="2as4xvdotevc6">
          <h3 className="mb-2 flex items-center gap-2 text-sm font-extrabold" data-id="element-458" data-mp="2armu7vztpeu8">
            <GiftIcon className="h-4 w-4 text-bonus-ink" aria-hidden="true" data-id="element-459" data-mp="25tlbjc6b7hja" /> Zahteve za nagrade ({rewards.length})
          </h3>
          <ul className="space-y-2" data-id="element-460" data-mp="2kh6g7aiu1otb">
            {rewards.map(r => {
          const m = memberById(r.memberId);
          const eligible = family.lastWeekReached[r.memberId];
          const balance = balanceOf(r.memberId);
          return <li key={r.id} className="rounded-2xl border-[1.5px] border-line p-3" data-id="element-461" data-mp="2phkqz0btjcv1">
                  <div className="flex items-start gap-3" data-id="element-462" data-mp="munhiy80uvwl">
                    {m && <Avatar member={m} size="sm" data-id="element-463" data-mp="mudot2vlean7" />}
                    <span className="min-w-0 flex-1" data-id="element-464" data-mp="rt72owesk3nw">
                      <span className="block font-bold" data-id="element-465" data-mp="rrvxqt9m0pv7">
                        {m?.name}: {r.title}
                      </span>
                      <span className="block text-xs text-muted" data-id="element-466" data-mp="11ja6zsrd541j">
                        {r.slot} · {r.cost} točk · stanje {balance}
                      </span>
                      <span className={`mt-1 block text-xs font-semibold ${eligible ? 'text-done-ink' : 'text-review-ink'}`} data-id="element-467" data-mp="3xm7gyzm25ls">
                        {eligible ? 'Prag v zadnjem zaključenem tednu dosežen' : 'Prag v zadnjem tednu ni dosežen'}
                      </span>
                    </span>
                  </div>
                  {!eligible && <label className="mt-2 flex items-center gap-2 text-sm" data-id="element-468" data-mp="1p9w3cms08zvp">
                      <input type="checkbox" className="h-4 w-4 accent-[#C24E28]" checked={Boolean(override[r.id])} onChange={e => setOverride(o => ({
                ...o,
                [r.id]: e.target.checked
              }))} data-id="element-469" data-mp="1tafzbn0udxg9" />
                      Izjema po presoji starša
                    </label>}
                  <div className="mt-2 flex justify-end gap-2" data-id="element-470" data-mp="39tj5cgffh9ln">
                    <Button size="sm" variant="secondary" onClick={() => rejectReward(r.id, 'Tokrat ne.')} data-id="element-471" data-mp="34yvp14ukvli5">
                      Zavrni
                    </Button>
                    <Button size="sm" variant="bonus" disabled={balance < r.cost || !eligible && !override[r.id]} onClick={() => approveReward(r.id, Boolean(override[r.id]))} data-id="element-472" data-mp="2v2ivq82y39nx">
                      Odobri (−{r.cost})
                    </Button>
                  </div>
                </li>;
        })}
          </ul>
        </div>}

      {toRedeem.length > 0 && <div data-id="element-473" data-mp="kar4cgca1gms">
          <h3 className="mb-2 text-sm font-extrabold" data-id="element-474" data-mp="kahecsr8f9h3">Odobrene nagrade – za koriščenje</h3>
          <ul className="divide-y divide-line rounded-2xl border-[1.5px] border-line" data-id="element-475" data-mp="fal5og7latv7">
            {toRedeem.map(r => <li key={r.id} className="flex flex-wrap items-center gap-3 px-3 py-2.5" data-id="element-476" data-mp="yey6b04c8pos">
                <span className="min-w-0 flex-1 text-sm" data-id="element-477" data-mp="137hsuml06v5q">
                  <b data-id="element-478" data-mp="pf1p1jka2t3r">{memberById(r.memberId)?.name}</b>: {r.title} · {r.slot}
                  <span className="block text-xs text-muted" data-id="element-479" data-mp="pcyo44hl7fk9">Točke so že odštete ob odobritvi.</span>
                </span>
                <Button size="sm" variant="secondary" onClick={() => redeemReward(r.id)} data-id="element-480" data-mp="1i3qprhhf8wez">
                  Označi kot koriščeno
                </Button>
              </li>)}
          </ul>
        </div>}

      {exceptions.length > 0 && <div data-id="element-481" data-mp="18516pldsh5x5">
          <h3 className="mb-2 flex items-center gap-2 text-sm font-extrabold" data-id="element-482" data-mp="187nf1n8i7pgf">
            <ShieldAlertIcon className="h-4 w-4 text-info-ink" aria-hidden="true" data-id="element-483" data-mp="1d32pe57az383" /> Predlogi izjem ({exceptions.length})
          </h3>
          <ul className="space-y-2" data-id="element-484" data-mp="3sy4lz13liqun">
            {exceptions.map(e => <li key={e.id} className="rounded-2xl border-[1.5px] border-line p-3" data-id="element-485" data-mp="1qs74sq6qphw">
                <p className="font-bold" data-id="element-486" data-mp="3q9skgbgmuvgw">
                  {memberById(e.memberId)?.name}: {exceptionTypeLabels[e.type]} · {formatDate(e.from)}–{formatDate(e.to)}
                </p>
                <p className="text-sm text-muted" data-id="element-487" data-mp="35l1hmssrxjt">„{e.note}“</p>
                <p className="mt-1 text-xs text-muted" data-id="element-488" data-mp="3ud72t8s1nnxi">Oprosti: {e.excuses.map(x => excuseLabels[x]).join(', ')}</p>
                <div className="mt-2 flex justify-end gap-2" data-id="element-489" data-mp="36ml78gz87w4e">
                  <Button size="sm" variant="secondary" onClick={() => decideException(e.id, false)} data-id="element-490" data-mp="36l096hdpdv1s">
                    Zavrni
                  </Button>
                  <Button size="sm" variant="success" onClick={() => decideException(e.id, true)} data-id="element-491" data-mp="3gdxzvasumqbh">
                    Odobri
                  </Button>
                </div>
              </li>)}
          </ul>
        </div>}
    </div>;
}